/**
 * TournamentPuzzleUpload
 *
 * Host-only panel for attaching a custom puzzle set (.pgn) to a SCHEDULED
 * Puzzle Royale tournament. Opened from the "Custom puzzles" button on a
 * scheduled row in HostTournamentPanel.
 *
 * Why upload is separate from create: the puzzle rows are keyed on the
 * tournament id, and create happens over the socket (which cannot carry a file),
 * so the host schedules first and uploads second. Uploading is allowed any time
 * while the tournament is still `scheduled` — including long after creation.
 *
 * Two behaviours worth knowing, both deliberate:
 *   * All-or-nothing. If any game in the file fails to parse, NOTHING is saved
 *     and every failure is listed by game number. A host must never end up with
 *     47 puzzles believing they uploaded 50.
 *   * Never blocks the race. A tournament flagged for custom puzzles with an
 *     empty set falls back to normal difficulty-band puzzles, so a forgotten or
 *     failed upload degrades instead of breaking. The status line says so
 *     explicitly rather than leaving the host to guess.
 *
 * Visual language mirrors HostTournamentPanel (teal accents, soft tint cards,
 * MaterialCommunityIcons) so it reads as part of the same tab.
 */

import React, {useMemo, useRef, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';

import {
  useClearTournamentPuzzles,
  useTournamentPuzzleStatus,
  useUploadTournamentPuzzles,
  type TournamentPuzzleError,
} from '@/Hooks/FunChallengesApi/useTournamentPuzzles';

const TEAL = '#3DAB9E';
const TEAL_TINT_SOFT = 'rgba(61, 171, 158, 0.06)';
const TEXT_DARK = '#262322';
const BORDER = 'rgba(38, 35, 34, 0.1)';
const WARN = '#B4761E';
const WARN_TINT = 'rgba(180, 118, 30, 0.08)';
const DANGER = '#B4462F';
const DANGER_TINT = 'rgba(180, 70, 47, 0.07)';

interface Props {
  tournamentId: number;
  /** Tournament's own flag. Used only for the "flagged but empty" warning —
   *  the authoritative count comes from the status query. */
  customPuzzlesFlag?: boolean;
  /** False once the tournament is live/completed: puzzles are frozen then,
   *  because the sequence is already materialised server-side. */
  editable?: boolean;
}

export default function TournamentPuzzleUpload({
  tournamentId,
  customPuzzlesFlag,
  editable = true,
}: Props) {
  const [bookName, setBookName] = useState<string>('');
  const [forceNoSetup, setForceNoSetup] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<TournamentPuzzleError[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [okMsg, setOkMsg] = useState<string>('');
  const inputRef = useRef<any>(null);

  const {data: status, isLoading} = useTournamentPuzzleStatus(tournamentId);
  const upload = useUploadTournamentPuzzles(tournamentId);
  const clear = useClearTournamentPuzzles(tournamentId);

  const count = status?.puzzle_count ?? 0;
  const flagged = status?.custom_puzzles ?? !!customPuzzlesFlag;

  // The one state a host must not misread: custom is ON but nothing is attached,
  // so the race will quietly use normal puzzles. Shown persistently (not as a
  // toast) precisely because it is easy to miss at create time.
  const flaggedButEmpty = flagged && count === 0;

  const resetMessages = () => {
    setErrors([]);
    setErrorMsg('');
    setOkMsg('');
  };

  const onPickFile = (e: any) => {
    resetMessages();
    const picked: File | undefined = e?.target?.files?.[0];
    setFile(picked || null);
  };

  const doUpload = () => {
    if (!file) {
      setErrorMsg('Choose a .pgn file first.');
      return;
    }
    resetMessages();
    upload.mutate(
      {file, bookName: bookName.trim() || undefined, forceNoSetup},
      {
        onSuccess: res => {
          const shape =
            res.no_setup_opponent_first > 0
              ? ` · ${res.no_setup_solver_first} solver-first, ${res.no_setup_opponent_first} opponent-first`
              : '';
          setOkMsg(`${res.count} puzzles attached${shape}`);
          setFile(null);
          // Clear the native input so re-picking the same filename re-fires
          // onChange (browsers suppress it for an identical value).
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        },
        onError: err => {
          setErrorMsg(err?.message || 'Upload failed.');
          setErrors(err?.errors || []);
        },
      },
    );
  };

  const doClear = () => {
    resetMessages();
    clear.mutate(undefined, {
      onSuccess: r => setOkMsg(`Removed ${r.removed} puzzle(s).`),
      onError: () => setErrorMsg('Could not remove the puzzle set.'),
    });
  };

  const statusLine = useMemo(() => {
    if (isLoading) {
      return 'Checking…';
    }
    if (count > 0) {
      const name = status?.puzzle_book_name;
      return name
        ? `${count} puzzles attached (${name})`
        : `${count} puzzles attached`;
    }
    if (flagged) {
      return 'Custom puzzles ON — none uploaded, the race will use normal puzzles';
    }
    return 'No custom puzzles — the race will use the difficulty band';
  }, [isLoading, count, flagged, status?.puzzle_book_name]);

  return (
    <View style={styles.wrap}>
      {/* Persistent state, never a transient toast — this is the line that
          prevents "I thought I'd uploaded them". */}
      <View
        style={[
          styles.statusBox,
          count > 0
            ? styles.statusOk
            : flaggedButEmpty
            ? styles.statusWarn
            : styles.statusNeutral,
        ]}>
        <MaterialCommunityIcons
          name={
            count > 0
              ? 'checkbox-marked-circle-outline'
              : flaggedButEmpty
              ? 'alert-outline'
              : 'information-outline'
          }
          size={16}
          color={count > 0 ? TEAL : flaggedButEmpty ? WARN : CCColors.TextColor.GreyText}
        />
        <CCText
          fontSize={FontSizes[12]}
          color={count > 0 ? TEXT_DARK : flaggedButEmpty ? WARN : CCColors.TextColor.GreyText}
          style={styles.statusText}>
          {statusLine}
        </CCText>
      </View>

      {!editable ? (
        <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.GreyText}>
          Puzzles are locked once the tournament starts.
        </CCText>
      ) : (
        <>
          <CCText style={styles.label}>Puzzle book name (optional)</CCText>
          {/* react-native-web renders these as real DOM nodes; a plain input is
              used (rather than TextInput) to match the file input beside it and
              the rest of this panel's web-only inputs. */}
          <input
            type="text"
            value={bookName}
            maxLength={200}
            placeholder="e.g. Mate in 2 — Beginner"
            onChange={(e: any) => setBookName(e.target.value)}
            style={inputStyle as any}
          />

          <CCText style={styles.label}>PGN file</CCText>
          <input
            ref={inputRef}
            type="file"
            accept=".pgn,application/x-chess-pgn,text/plain"
            onChange={onPickFile}
            style={fileInputStyle as any}
          />

          <Pressable
            onPress={() => setForceNoSetup(v => !v)}
            style={styles.checkRow}>
            <MaterialCommunityIcons
              name={forceNoSetup ? 'checkbox-marked' : 'checkbox-blank-outline'}
              size={18}
              color={forceNoSetup ? TEAL : CCColors.TextColor.GreyText}
            />
            <View style={{flex: 1}}>
              <CCText fontSize={FontSizes[12]} color={TEXT_DARK}>
                Solver moves first in every puzzle
              </CCText>
              <CCText
                fontSize={FontSizes[10]}
                color={CCColors.TextColor.GreyText}>
                Normally detected per puzzle from the solution length. Tick this
                only if your file is read the wrong way round.
              </CCText>
            </View>
          </Pressable>

          <View style={styles.actions}>
            <CCButton
              type="fill"
              onPress={doUpload}
              disabled={!file || upload.isLoading}>
              <CCText fontSize={FontSizes[12]} color={CCColors.White}>
                {upload.isLoading ? 'Uploading…' : 'Upload puzzles'}
              </CCText>
            </CCButton>
            {count > 0 ? (
              <CCButton
                type="outline"
                onPress={doClear}
                disabled={clear.isLoading}>
                <CCText fontSize={FontSizes[12]} color={DANGER}>
                  {clear.isLoading ? 'Removing…' : 'Remove set'}
                </CCText>
              </CCButton>
            ) : null}
          </View>
        </>
      )}

      {okMsg ? (
        <View style={[styles.msgBox, styles.msgOk]}>
          <CCText fontSize={FontSizes[12]} color={TEAL}>
            {okMsg}
          </CCText>
        </View>
      ) : null}

      {errorMsg ? (
        <View style={[styles.msgBox, styles.msgErr]}>
          <CCText fontSize={FontSizes[12]} color={DANGER}>
            {errorMsg}
          </CCText>
          {errors.length > 0 ? (
            // Per-game detail, verbatim from the server. Scrolls rather than
            // truncating: a host fixing a file needs every failing game number,
            // and a long list is still more useful than a clipped one.
            <ScrollView style={styles.errList} nestedScrollEnabled>
              {errors.map(e => (
                <CCText
                  key={`${e.game}-${e.error}`}
                  fontSize={FontSizes[11]}
                  color={CCColors.TextColor.GreyText}
                  style={styles.errRow}>
                  {`Game ${e.game}: ${e.error}`}
                </CCText>
              ))}
            </ScrollView>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${BORDER}`,
  fontSize: 13,
  color: TEXT_DARK,
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
};

const fileInputStyle = {
  width: '100%',
  fontSize: 12,
  color: TEXT_DARK,
};

const styles = StyleSheet.create({
  wrap: {gap: 8},
  statusBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  statusOk: {backgroundColor: TEAL_TINT_SOFT, borderColor: 'rgba(61,171,158,0.3)'},
  statusWarn: {backgroundColor: WARN_TINT, borderColor: 'rgba(180,118,30,0.35)'},
  statusNeutral: {backgroundColor: 'transparent', borderColor: BORDER},
  statusText: {flex: 1},
  label: {
    fontSize: FontSizes[11],
    color: CCColors.TextColor.GreyText,
    marginTop: 4,
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 4,
  },
  actions: {flexDirection: 'row', gap: 8, marginTop: 8, flexWrap: 'wrap'},
  msgBox: {
    marginTop: 4,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  msgOk: {backgroundColor: TEAL_TINT_SOFT, borderColor: 'rgba(61,171,158,0.3)'},
  msgErr: {backgroundColor: DANGER_TINT, borderColor: 'rgba(180,70,47,0.3)'},
  errList: {maxHeight: 160, marginTop: 6},
  errRow: {marginBottom: 2},
});
